import React, { useState } from 'react';

const Research = () => {
  // Categories for filtering
  const categories = [
    "All", 
    "Sequestration", 
    "Policy & Programs", 
    "Tools & Resources", 
    "Education"
  ];
  
  const [activeCategory, setActiveCategory] = useState("All");
  
  // Articles data with category tags
  const articles = [
    {
      id: 1,
      title: "Carbon Sequestration in Agricultural Soils",
      source: "Environmental Science & Policy",
      link: "https://doi.org/10.1016/j.envsci.2016.04.010",
      category: "Sequestration",
      year: "2016"
    },
    {
      id: 2,
      title: "Soil Carbon Sequestration Potential in Global Croplands",
      source: "National Library of Medicine",
      link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9308964/",
      category: "Sequestration",
      year: "2022"
    },
    {
      id: 3,
      title: "Agroforestry Systems for Carbon Sequestration",
      source: "Nature Communications",
      link: "https://doi.org/10.1038/s41467-020-19578-z",
      category: "Sequestration",
      year: "2020"
    },
    {
      id: 4,
      title: "IPCC Special Report: Climate Change and Land (Chapter 2)",
      source: "Intergovernmental Panel on Climate Change (IPCC)",
      link: "https://www.ipcc.ch/srccl/chapter/chapter-2/",
      category: "Policy & Programs",
      year: "2019"
    },
    {
      id: 5,
      title: "Assessment on Agriculture and Forestry in Carbon Markets",
      source: "USDA",
      link: "https://www.usda.gov/about-usda/news/press-releases/2023/10/23/usda-releases-assessment-agriculture-and-forestry-carbon-markets",
      category: "Sequestration",
      year: "2023"
    },
    {
      id: 6,
      title: "Soil Organic Carbon – The Hidden Potential",
      source: "Food and Agriculture Organization (FAO)",
      link: "https://www.fao.org/3/i6937en/i6937en.pdf",
      category: "Sequestration",
      year: "2017"
    },
    {
      id: 7,
      title: "State and Trends of Carbon Pricing (2023)",
      source: "World Bank",
      link: "https://openknowledge.worldbank.org/handle/10986/39822",
      category: "Policy & Programs",
      year: "2023"
    },
    {
      id: 8,
      title: "Agricultural Land Management (ALM) Projects",
      source: "Verra (Verified Carbon Standard)",
      link: "https://verra.org/methodologies/vm0042-improved-agricultural-land-management-v2-1/#overview",
      category: "Policy & Programs",
      year: "2023"
    },
    {
      id: 9,
      title: "California's Cap-and-Trade Program",
      source: "California Air Resources Board",
      link: "https://ww2.arb.ca.gov/our-work/programs/cap-and-trade-program",
      category: "Policy & Programs",
      year: "2023"
    },
    {
      id: 10,
      title: "COMET-Farm Tool",
      source: "USDA",
      link: "https://www.usda.gov/about-usda/news/blog/comet-farmtm-conservation-calculation",
      category: "Tools & Resources",
      year: "2023"
    },
    {
      id: 11,
      title: "Cool Farm Tool",
      source: "Cool Farm Alliance",
      link: "https://coolfarmtool.org/",
      category: "Tools & Resources",
      year: "2023"
    },
    {
      id: 12,
      title: "Carbon Farming 101",
      source: "Penn State Extension",
      link: "https://extension.psu.edu/carbon-markets-101",
      category: "Education",
      year: "2022"
    },
    {
      id: 13,
      title: "Carbon Markets for Farmers",
      source: "Iowa State University Extension",
      link: "https://www.extension.iastate.edu/agdm/crops/html/a1-70.html",
      category: "Policy & Programs",
      year: "2023"
    },
    {
      id: 14,
      title: "Agricultural Carbon Research",
      source: "CarbonPlan",
      link: "https://carbonplan.org/research",
      category: "Sequestration",
      year: "2023"
    },
    {
      id: 15,
      title: "Soil Health Research Library",
      source: "Soil Health Institute",
      link: "https://soilhealthinstitute.org/resources/",
      category: "Tools & Resources",
      year: "2023"
    }
  ];
  
  // Filter articles based on active category
  const filteredArticles = activeCategory === "All" 
    ? articles 
    : articles.filter(article => article.category === activeCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-teal-50 p-6 md:p-12">
      {/* Header with blur effect */}
      <div className="sticky top-0 z-10 backdrop-blur-lg bg-white/70 rounded-2xl p-6 mb-8 shadow-sm">
        <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-teal-400 to-green-300 bg-clip-text text-transparent">
          Research & Scientific Articles
        </h1>
        <p className="text-gray-600 mt-3 max-w-3xl">
          Explore the latest scientific research and publications on carbon farming, soil sequestration, and agricultural climate solutions.
        </p>
      </div>

      {/* Filter tabs */}
      <div className="mb-8 overflow-x-auto pb-2">
        <div className="flex space-x-2 backdrop-blur-md bg-white/50 p-2 rounded-xl inline-block">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-4 py-2 rounded-lg transition-all ${
                activeCategory === category
                  ? "bg-teal-400 text-white shadow-sm"
                  : "hover:bg-teal-50 text-gray-600"
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Articles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredArticles.map(article => (
          <a
            key={article.id}
            href={article.link}
            target="_blank"
            rel="noopener noreferrer"
            className="group"
          >
            <div className="h-full bg-white/80 backdrop-blur-md rounded-3xl p-6 shadow-sm transition-all hover:shadow-md hover:translate-y-1 border border-teal-100 flex flex-col">
              <div className="flex justify-between items-start mb-2">
                <span className="text-xs font-medium bg-teal-100 text-teal-700 px-3 py-1 rounded-full">
                  {article.category}
                </span>
                <span className="text-xs text-gray-500">{article.year}</span>
              </div>
              
              <h2 className="text-xl font-semibold text-gray-800 mb-2 group-hover:text-teal-600 transition-colors flex-grow">
                {article.title}
              </h2>
              
              <div className="mt-4 flex justify-between items-center">
                <p className="text-sm text-gray-500">{article.source}</p>
                <div className="text-teal-500 transform transition-transform group-hover:translate-x-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="7" y1="17" x2="17" y2="7"></line>
                    <polyline points="7 7 17 7 17 17"></polyline>
                  </svg>
                </div>
              </div>
            </div>
          </a>
        ))}
      </div>

      {/* Featured research section */}
      <section className="mt-12 bg-white/80 backdrop-blur-md rounded-3xl p-8 shadow-md">
        <h2 className="text-2xl font-semibold text-teal-600 mb-6">Why Research Matters</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-6 shadow-sm">
            <div className="text-3xl mb-3">📊</div>
            <h3 className="text-lg font-medium text-teal-700">Data-Driven Decisions</h3>
            <p className="text-gray-600 mt-2">Scientific research provides evidence-based insights for implementing effective carbon farming practices.</p>
          </div>
          <div className="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-6 shadow-sm">
            <div className="text-3xl mb-3">🔍</div>
            <h3 className="text-lg font-medium text-teal-700">Validation Methods</h3>
            <p className="text-gray-600 mt-2">Studies help establish reliable methods for measuring and verifying carbon sequestration in soils.</p>
          </div>
          <div className="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-6 shadow-sm">
            <div className="text-3xl mb-3">💡</div>
            <h3 className="text-lg font-medium text-teal-700">Innovation Catalyst</h3>
            <p className="text-gray-600 mt-2">Research drives the development of new technologies and approaches for enhancing carbon capture in agriculture.</p>
          </div>
        </div>
      </section>

      {/* Call to action */}
      <div className="mt-12 bg-gradient-to-r from-teal-400 to-green-300 rounded-3xl p-8 shadow-md text-white text-center">
        <h2 className="text-2xl font-semibold mb-3">Stay Updated with the Latest Research</h2>
        <p className="mb-6 max-w-2xl mx-auto">Join our newsletter to receive updates on new studies, tools, and developments in carbon farming science.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
          <input 
            type="email" 
            placeholder="Your email address" 
            className="px-4 py-3 rounded-full text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-300 w-full"
          />
          <button className="bg-white text-teal-600 px-6 py-3 rounded-full font-medium hover:bg-teal-50 transition-colors shadow-sm whitespace-nowrap">
            Subscribe
          </button>
        </div>
      </div>

    </div>
  );
};

export default Research;